/**
 * The pandit KYC queue — where the product's central trust claim is actually made.
 *
 * **Six badges, decided independently.** There is no "approve the pandit" button that lights
 * everything up: identity can pass while bank fails, and the profile then shows exactly that. A
 * generic "Verified" tick would claim something nobody checked, which is the shortcut this
 * marketplace exists to avoid (R41).
 *
 * **Every badge names the evidence that justified it.** The server validates that evidence is in
 * its passing state *at the moment of issue* — a document approved and later superseded, or a
 * background check that has since lapsed, is refused. The evidence picker here offers only what
 * that badge's rule accepts, and the server refuses anything else regardless.
 *
 * **Approving the case is a separate act from issuing badges**, and the server refuses approval
 * until `identity_verified` and `bank_verified` both exist. It will not mint them as a side
 * effect: a badge is a statement about specific evidence, and creating one because a case was
 * approved would invert that. The screen shows the refusal rather than pre-empting it.
 *
 * **Opening a document is audited before the link exists.** The view button calls the server,
 * which writes the audit row and then returns a short-lived URL — "who looked at this person's
 * identity papers, and when" is a question both a regulator and the pandit are entitled to ask.
 *
 * **Revoking one badge never touches another.** That independence is the requirement: a lapsed
 * background check must not quietly un-verify somebody's bank account, and the six state machines
 * move separately on purpose.
 */
import { useState } from 'react';
import { Badge, BADGE_TYPES, Button, Chip, Note, StatusChip, type BadgeState, type BadgeType } from '../components/primitives';
import { Select } from '../components/forms';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { ConfirmAction, FilterBar, ScopeAction, Section } from '../components/kit';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { QUEUE_HINT, useQueue } from '../lib/useQueue';
import { count } from '../lib/text';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { KycRejectionTemplates, liveQuickPicks } from './KycRejectionTemplates';
import {
  useDecideCase,
  useIssueBadge,
  useKycCase,
  useKycHistory,
  useKycQueue,
  useRecordReferenceOutcome,
  useRejectionTemplates,
  useReopenCase,
  useReviewDocument,
  useRevokeBadge,
  useViewDocument,
} from '../lib/api/hooks/kyc';
import { BackgroundChecks } from './KycBackgroundChecks';
import { useUrlFilters, type FilterDef } from '../lib/filterCodecs';
import { formatDateTime } from '../lib/datetime';
import { TrailTable } from '../components/audit';

const PAGE_SIZE = 50;

/**
 * Server-side status filters. `GET /v1/admin/kyc/queue` takes `status`, `city_id`, `page` and
 * `page_size` — there is no text search, and a client-side one over a single page would look
 * through fifty cases and confidently report no match for the pandit on page two.
 */
const TABS = [
  { key: '', label: 'All open' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'under_review', label: 'Under review' },
  { key: 'docs_pending', label: 'Awaiting documents' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
] as const;

/** The queue's tab lives in the URL (`?kyc_status=`), so a pasted link opens the same queue. */
const KYC_FILTERS = {
  status: { kind: 'select', options: TABS.map((t) => t.key).filter(Boolean) },
} as const satisfies Record<string, FilterDef>;

/**
 * What each badge is allowed to rest on. This mirrors the server's `EVIDENCE_MATRIX` so the picker
 * offers the right *kind* of evidence — it does not re-implement the rule. The server validates
 * state (approved, positive, clear, live) and refuses anything that does not hold, which is the
 * only check that counts.
 */
const EVIDENCE_KIND = {
  identity_verified: { field: 'evidence_document_id', needs: 'an approved government id, photo and live video' },
  bank_verified: { field: 'evidence_document_id', needs: 'an approved bank proof and a confirmed penny drop' },
  training_reference_verified: { field: 'evidence_reference_check_id', needs: 'two positive references' },
  platform_quality_checked: { field: 'evidence_document_id', needs: 'an ops quality-review artefact' },
  temple_authorized: { field: 'temple_authorization_id', needs: 'a live temple agreement' },
  background_verified: { field: 'evidence_background_check_id', needs: 'a background check returning clear' },
} as const satisfies Record<BadgeType, { field: string; needs: string }>;

/** The two the matcher treats as mandatory. Without both, no offer ever reaches this pandit. */
const MANDATORY: readonly BadgeType[] = ['identity_verified', 'bank_verified'];

const DETAIL_TABS = ['Documents', 'References & checks', 'Badges', 'Decision', 'History'];

const DOC_TONE: Record<string, 'success' | 'warn' | 'error' | 'neutral'> = {
  approved: 'success',
  pending: 'warn',
  rejected: 'error',
  superseded: 'neutral',
};

export function documentTone(status: string): 'success' | 'warn' | 'error' | 'neutral' {
  return DOC_TONE[status] ?? 'neutral';
}

/** Bytes as an operator reads them. A reviewer scanning a queue does not want `1048576`. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function KycQueue() {
  const { scope, toast } = useConsole();
  const [filters, setFilters] = useUrlFilters('kyc', KYC_FILTERS);
  const tab = filters.status ?? '';
  const setTab = (key: string) => setFilters({ status: key || undefined });
  const [detailTab, setDetailTab] = useState('Documents');
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [deciding, setDeciding] = useState<'approved' | 'rejected' | null>(null);
  const [reopening, setReopening] = useState(false);
  const [revoking, setRevoking] = useState<number | null>(null);
  const [badgeType, setBadgeType] = useState<BadgeType>('identity_verified');
  const [evidenceId, setEvidenceId] = useState('');

  const queue = useKycQueue({ page: 1, page_size: PAGE_SIZE, status: tab || undefined });
  /* `useQueue` keys on `id`; a queue row's identity is its case. Mapped rather than renamed on
     the server — the field is `case_id` because that is what it is. */
  const rows = (queue.data?.items ?? []).map((r) => ({ ...r, id: r.case_id }));
  const { focus, setFocus, open, setOpen } = useQueue(rows, { key: tab });

  const detail = useKycCase(open?.case_id);
  const reviewDocument = useReviewDocument();
  const viewDocument = useViewDocument();
  // The minted link, per document. Shown, not opened for the reviewer: a tab opened after an
  // await is a popup the browser blocks, and the expiry has to be read beside the link anyway.
  const [links, setLinks] = useState<Record<number, { url: string; expires_at: string }>>({});
  const issueBadge = useIssueBadge();
  const revokeBadge = useRevokeBadge();
  const decideCase = useDecideCase();
  const reopenCase = useReopenCase();
  const recordOutcome = useRecordReferenceOutcome();
  const templates = useRejectionTemplates();

  const outcome = (message: string, tone: 'success' | 'info' | 'error' = 'success') => ({
    onSuccess: () => toast({ tone, message }),
    onError: (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) }),
  });

  const rejectDialog = (
    <ConfirmAction
      open={rejecting !== null}
      title="Reject this document?"
      phrase="REJECT"
      tone="error"
      reason="What the pandit must fix (reaches them verbatim)"
      quickPicks={liveQuickPicks(templates.data?.items)}
      consequences={[
        'Your reason is sent to the pandit word for word, so it has to be something they can act on.',
        'They re-upload; the rejected row is kept and superseded, never deleted.',
      ]}
      onCancel={() => setRejecting(null)}
      onConfirm={(reason: string) => {
        const id = rejecting;
        setRejecting(null);
        if (id !== null) {
          reviewDocument.mutate({ documentId: id, decision: 'rejected', reason }, outcome('Document rejected', 'info'));
        }
      }}
      confirmLabel="Reject document"
    />
  );

  const decideDialog = open && deciding && (
    <ConfirmAction
      open
      title={deciding === 'approved' ? 'Approve this verification?' : 'Reject this verification?'}
      phrase={deciding === 'approved' ? 'APPROVE' : 'REJECT'}
      tone={deciding === 'approved' ? 'warn' : 'error'}
      reason={deciding === 'rejected'}
      consequences={
        deciding === 'approved'
          ? [
              'Approval does NOT issue badges. The server refuses this until identity_verified and bank_verified already exist, because a badge is a statement about evidence somebody checked.',
              'Without both, no offer ever reaches this pandit regardless of the case status.',
            ]
          : [
              'Sends the pandit back to draft with the checklist reopened.',
              'Your reason reaches them verbatim.',
            ]
      }
      onCancel={() => setDeciding(null)}
      onConfirm={(reason: string) => {
        const decision = deciding;
        setDeciding(null);
        decideCase.mutate(
          { caseId: open.case_id, decision, reason },
          outcome(decision === 'approved' ? 'Verification approved' : 'Verification rejected', decision === 'approved' ? 'success' : 'info'),
        );
      }}
      confirmLabel={deciding === 'approved' ? 'Approve' : 'Reject'}
    />
  );

  const reopenDialog = open && (
    <ConfirmAction
      open={reopening}
      title="Open a re-verification?"
      phrase="REOPEN"
      reason
      consequences={[
        'Creates a NEW case. The previous decision, its documents and the reasons they were accepted stay exactly as they were.',
        'Nothing links backwards — reopening the old case would rewrite the record of what was approved and when.',
      ]}
      onCancel={() => setReopening(false)}
      onConfirm={(reason: string) => {
        setReopening(false);
        reopenCase.mutate({ caseId: open.case_id, reason }, outcome('Re-verification opened'));
      }}
      confirmLabel="Reopen"
    />
  );

  const revokeDialog = (
    <ConfirmAction
      open={revoking !== null}
      title="Revoke this badge?"
      phrase="REVOKE"
      tone="error"
      reason
      consequences={[
        'Revoking one badge never touches another — a lapsed background check must not quietly un-verify somebody’s bank account.',
        'If this is identity or bank, the pandit stops receiving offers immediately: those two are what the matcher requires.',
        'The badge is kept and marked revoked with your reason, never deleted. A later dispute is argued from that row.',
      ]}
      onCancel={() => setRevoking(null)}
      onConfirm={(reason: string) => {
        const id = revoking;
        setRevoking(null);
        if (id !== null) {
          revokeBadge.mutate({ badgeId: id, status: 'revoked', reason }, outcome('Badge revoked', 'info'));
        }
      }}
      confirmLabel="Revoke badge"
    />
  );

  if (open) {
    const c = detail.data;
    const held = new Set((c?.badges ?? []).filter((b) => b.status === 'active').map((b) => b.badge_type));
    const missing = MANDATORY.filter((m) => !held.has(m));

    return (
      <DetailPage
        title={open.display_name}
        code={open.public_code}
        status={<StatusChip tone={documentTone(c?.case.status ?? '')} label={(c?.case.status ?? open.status).replace(/_/g, ' ')} />}
        tabs={DETAIL_TABS}
        activeTab={detailTab}
        onTab={setDetailTab}
        onClose={() => setOpen(null)}
      >
        {detail.isPending && <LoadingState message="Reading the case…" />}
        {detail.isError && <ErrorState title="Could not read this case" cause={errorMessage(detail.error)} />}

        {c && detailTab === 'Documents' && (
          <Section title="Documents">
            <Note>
              Superseded rows are kept: the history is what a later dispute is argued from.
            </Note>
            {c.documents.length === 0 ? (
              <EmptyState icon="file-text" title="Nothing uploaded yet" inline />
            ) : (
              <div style={{ display: 'grid', gap: 'var(--sp-xs)', marginTop: 'var(--sp-md)' }}>
                {c.documents.map((d) => (
                  <div
                    key={d.id}
                    style={{
                      display: 'flex',
                      gap: 'var(--sp-sm)',
                      alignItems: 'center',
                      padding: 'var(--sp-sm) var(--sp-md)',
                      border: '1px solid var(--c-border)',
                      borderRadius: 'var(--r-tile)',
                      background: 'var(--c-surface2)',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{d.doc_type.replace(/_/g, ' ')}</div>
                      <div style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                        {fileSize(d.file_size_bytes)} · {d.content_type}
                        {d.number_last4 ? ` · ends ${d.number_last4}` : ''}
                      </div>
                      {links[d.id] && (
                        <a
                          href={links[d.id]!.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 'var(--fs-admin-colhead)' }}
                        >
                          Open the {d.doc_type.replace(/_/g, ' ')} · link valid until{' '}
                          {formatDateTime(links[d.id]!.expires_at)}
                        </a>
                      )}
                      {d.rejection_reason && (
                        <div style={{ color: 'var(--c-error-text)', fontSize: 'var(--fs-admin-colhead)' }}>
                          {d.rejection_reason}
                        </div>
                      )}
                    </div>
                    <StatusChip tone={documentTone(d.review_status)} label={d.review_status} />
                    <ScopeAction
                      scope={scope}
                      need="supply_kyc"
                      size="sm"
                      onClick={() =>
                        viewDocument.mutate(d.id, {
                          onSuccess: (r) => setLinks((all) => ({ ...all, [d.id]: r })),
                          onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
                        })
                      }
                    >
                      View
                    </ScopeAction>
                    {d.review_status === 'pending' && (
                      <>
                        <ScopeAction
                          scope={scope}
                          need="supply_kyc"
                          size="sm"
                          onClick={() =>
                            reviewDocument.mutate({ documentId: d.id, decision: 'approved' }, outcome('Document approved'))
                          }
                        >
                          Approve
                        </ScopeAction>
                        <ScopeAction scope={scope} need="supply_kyc" size="sm" onClick={() => setRejecting(d.id)}>
                          Reject
                        </ScopeAction>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {c && detailTab === 'References & checks' && (
          <>
            <Section title="References">
              <Note>Ritual competency is what these calls establish. Two positives issue the badge.</Note>
              {c.references.length === 0 ? (
                <EmptyState icon="phone" title="No referees given yet" inline />
              ) : (
                <div style={{ display: 'grid', gap: 'var(--sp-xs)', marginTop: 'var(--sp-md)' }}>
                  {c.references.map((r) => (
                    <div
                      key={r.id}
                      style={{
                        display: 'flex',
                        gap: 'var(--sp-sm)',
                        alignItems: 'center',
                        padding: 'var(--sp-sm) var(--sp-md)',
                        border: '1px solid var(--c-border)',
                        borderRadius: 'var(--r-tile)',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>{r.referee_name}</div>
                        <div style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                          {r.relation.replace(/_/g, ' ')}
                        </div>
                      </div>
                      <StatusChip
                        tone={r.outcome === 'positive' ? 'success' : r.outcome === 'negative' ? 'error' : 'warn'}
                        label={r.outcome}
                      />
                      {r.outcome === 'pending' && (
                        <>
                          <ScopeAction
                            scope={scope}
                            need="supply_kyc"
                            size="sm"
                            onClick={() =>
                              recordOutcome.mutate({ referenceId: r.id, outcome: 'positive' }, outcome('Recorded positive'))
                            }
                          >
                            Positive
                          </ScopeAction>
                          <ScopeAction
                            scope={scope}
                            need="supply_kyc"
                            size="sm"
                            onClick={() =>
                              recordOutcome.mutate({ referenceId: r.id, outcome: 'negative' }, outcome('Recorded negative', 'info'))
                            }
                          >
                            Negative
                          </ScopeAction>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <BackgroundChecks caseId={c.case_id} checks={c.background_checks} />
          </>
        )}

        {c && detailTab === 'Badges' && (
          <>
            <Section title="Badges held">
              <Note>
                Six, decided independently. A generic “verified” tick would claim something nobody
                checked.
              </Note>
              <p style={{ marginTop: 'var(--sp-sm)', color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                {activeCount(c.badges)} of 6 active
              </p>
              <ul aria-label="The six badges" style={{ listStyle: 'none', padding: 0, margin: 'var(--sp-md) 0 0', display: 'grid', gap: 'var(--sp-sm)' }}>
                {(Object.keys(BADGE_TYPES) as BadgeType[]).map((t) => {
                  const { state, row } = badgeStanding(c.badges, t);
                  return (
                    <li key={t} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
                      <Badge type={t} state={state} />
                      <span style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                        {stateLine(state, row)}
                      </span>
                      {t === 'temple_authorized' ? (
                        <span style={{ color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-colhead)' }}>
                          Derived from an active temple authorization — manage it in the Temple console.
                        </span>
                      ) : (
                        state !== 'active' &&
                        forecastLine(c.issuability, t) && (
                          <span style={{ color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-colhead)' }}>
                            {forecastLine(c.issuability, t)}
                          </span>
                        )
                      )}
                      {state === 'active' && row && (
                        <ScopeAction
                          scope={scope}
                          need="supply_kyc"
                          size="sm"
                          onClick={() => setRevoking(row.id)}
                        >
                          Revoke
                        </ScopeAction>
                      )}
                    </li>
                  );
                })}
              </ul>
              {missing.length > 0 && (
                <p style={{ marginTop: 'var(--sp-md)', color: 'var(--c-warn-text)' }}>
                  Not dispatchable: missing {missing.map((m) => BADGE_TYPES[m].label).join(' and ')}.
                </p>
              )}

            </Section>

            <Section title="Issue a badge">
              <Note>
                Every badge names the evidence that justified it. The server checks that evidence is
                in its passing state now — not when it was uploaded.
              </Note>
              <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'flex-end', marginTop: 'var(--sp-md)', flexWrap: 'wrap' }}>
                <Select
                  label="Badge"
                  value={BADGE_TYPES[badgeType].label}
                  options={(Object.keys(BADGE_TYPES) as BadgeType[]).map((t) => BADGE_TYPES[t].label)}
                  onChange={(label) =>
                    setBadgeType(
                      (Object.keys(BADGE_TYPES) as BadgeType[]).find((t) => BADGE_TYPES[t].label === label)!,
                    )
                  }
                />
                <Select
                  label={`Evidence — ${EVIDENCE_KIND[badgeType].field.replace(/_/g, ' ')}`}
                  value={evidenceId}
                  options={evidenceOptions(c, badgeType)}
                  onChange={setEvidenceId}
                />
                <ScopeAction
                  scope={scope}
                  need="supply_kyc"
                  size="sm"
                  disabled={!evidenceId || refusal(c.issuability, badgeType) !== null}
                  onClick={() =>
                    issueBadge.mutate(
                      {
                        pandit_profile_id: c.pandit_profile_id,
                        badge_type: badgeType,
                        [EVIDENCE_KIND[badgeType].field]: Number(evidenceId.split(' · ')[0]),
                      } as never,
                      outcome(`${BADGE_TYPES[badgeType].label} issued`),
                    )
                  }
                >
                  Issue
                </ScopeAction>
              </div>
              <p style={{ marginTop: 'var(--sp-sm)', color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                {refusal(c.issuability, badgeType) ?? `Needs ${EVIDENCE_KIND[badgeType].needs}.`}
              </p>
            </Section>
          </>
        )}

        {c && detailTab === 'Decision' && (
          <Section title="Decide the case">
            <KV
              rows={[
                ['Status', c.case.status],
                ['Submitted', c.case.submitted_at ? formatDateTime(c.case.submitted_at) : 'not submitted'],
                ['Rejection reason', c.case.rejection_reason ?? '—'],
                ['Re-verification due', c.case.re_verification_due_on ?? '—'],
                ['Notes', c.case_notes ?? '—'],
              ]}
            />
            <ul aria-label="Approval readiness" style={{ listStyle: 'none', padding: 0, margin: 'var(--sp-md) 0 0', display: 'grid', gap: 'var(--sp-xs)' }}>
              {readiness(c.badges, c.issuability).map((r) => (
                <li key={r.type} style={{ color: r.held ? 'var(--c-ink)' : 'var(--c-warn-text)' }}>
                  {r.held ? '✓' : '✗'} {r.label} — {r.line}
                </li>
              ))}
            </ul>
            {missing.length > 0 && (
              <p style={{ marginTop: 'var(--sp-sm)', color: 'var(--c-warn-text)' }}>
                Approval is blocked until {missing.map((m) => BADGE_TYPES[m].label).join(' and ')} {missing.length > 1 ? 'are' : 'is'} issued.
              </p>
            )}
            <div style={{ display: 'flex', gap: 'var(--sp-sm)', marginTop: 'var(--sp-md)', flexWrap: 'wrap' }}>
              <ScopeAction scope={scope} need="supply_kyc" size="sm" disabled={missing.length > 0} onClick={() => setDeciding('approved')}>
                Approve verification
              </ScopeAction>
              <ScopeAction scope={scope} need="supply_kyc" size="sm" onClick={() => setDeciding('rejected')}>
                Reject verification
              </ScopeAction>
              <ScopeAction scope={scope} need="supply_kyc" size="sm" onClick={() => setReopening(true)}>
                Open re-verification
              </ScopeAction>
            </div>
          </Section>
        )}

        {detailTab === 'History' && <KycHistory panditId={open.pandit_profile_id} />}

        {revokeDialog}
        {rejectDialog}
        {decideDialog}
        {reopenDialog}
      </DetailPage>
    );
  }

  return (
    <>
      <FilterBar tabs={TABS.map((t) => ({ key: t.key, label: t.label }))} tab={tab} onTab={setTab} />

      {queue.isPending && <LoadingState message="Reading the queue…" />}
      {queue.isError && <ErrorState title="Could not read the KYC queue" cause={errorMessage(queue.error)} />}
      {queue.isSuccess && rows.length === 0 && (
        <EmptyState icon="file-check" title="Nothing in this queue" body="No case is in this status right now." />
      )}
      {rows.length > 0 && (
        <DataTable
          columns={[
            { key: 'display_name', label: 'Pandit' },
            { key: 'public_code', label: 'Code' },
            { key: 'status', label: 'Status' },
            { key: 'documents', label: 'Documents' },
          ]}
          rows={rows.map((r) => ({
            id: r.case_id,
            display_name: r.display_name,
            public_code: r.public_code,
            status: <StatusChip tone={documentTone(r.status)} label={r.status.replace(/_/g, ' ')} />,
            documents: (
              <Chip label={`${r.documents_approved} approved · ${r.documents_pending} pending`} />
            ),
          }))}
          focusedIndex={focus}
          onFocusRow={setFocus}
          onRowClick={(r) => setOpen(rows.find((x) => x.id === r.id)!)}
          footer={`${count(queue.data!.total, 'case')} · ${QUEUE_HINT}`}
        />
      )}
      <KycRejectionTemplates />
    </>
  );
}

/**
 * The ids this badge's rule can point at, drawn from the case in hand.
 *
 * The picker offers only the right *kind* — a reference badge cannot be evidenced by a document —
 * because offering the wrong kind teaches an operator that the refusal is arbitrary. Whether the
 * evidence is in a passing state is the server's call, and it refuses regardless of what is here.
 */
interface BadgeRecord {
  id: number;
  badge_type: string;
  status: string;
  issued_on: string;
  expires_on?: string | null;
  revocation_reason?: string | null;
  issued_by?: string | null;
}

interface Forecast {
  badge_type: string;
  issuable: boolean;
  reason?: string | null;
}

/**
 * The server's forecast for one badge (doc 05-admin/06 §3): the reason Issue would be refused, or
 * null when it would go through — or when the server sent no forecast, so the button stays live
 * and the issue route's own refusal is what the operator sees.
 */
/**
 * The case History tab (05-admin/06 §4 step 9, R310): every document, referee, badge and decision
 * act on this pandit, newest first. Read from the pandit's own KYC trail rather than the audit
 * log, which `supply_kyc` does not hold; a re-verification years later reads as one continuous
 * story, not a case that started from nothing.
 */
function KycHistory({ panditId }: { panditId: number }) {
  const history = useKycHistory(panditId);
  if (history.isPending) return <LoadingState message="Reading the history…" />;
  if (history.isError) return <ErrorState title="Could not read the history" cause={errorMessage(history.error)} />;
  const items = history.data.pages.flatMap((p) => p.items);
  return (
    <Section title="History" note="every KYC act on this pandit · newest first" pad={false}>
      {items.length === 0 ? (
        <EmptyState inline title="Nothing recorded" body="Every document, referee, badge and decision act will be listed here." />
      ) : (
        <TrailTable items={items} />
      )}
      {history.hasNextPage && (
        <div style={{ padding: 'var(--sp-sm) var(--sp-md)' }}>
          <Button size="sm" variant="secondary" disabled={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>
            {history.isFetchingNextPage ? 'Reading…' : 'Show older'}
          </Button>
        </div>
      )}
    </Section>
  );
}

export function refusal(forecast: readonly Forecast[] | undefined, type: BadgeType): string | null {
  const row = forecast?.find((f) => f.badge_type === type);
  return row && !row.issuable ? (row.reason ?? 'Not issuable yet.') : null;
}

/**
 * The mandatory-badge rule rendered before the Approve button (doc 05-admin/06 §3.7): the same
 * list a `409 mandatory_badges_missing` would return, so the operator never meets the rule as an
 * error. A badge counts only while it is active; one not held says what its evidence still lacks.
 */
export function readiness(
  badges: readonly BadgeRecord[],
  forecast: readonly Forecast[] | undefined,
): { type: BadgeType; label: string; held: boolean; line: string }[] {
  return MANDATORY.map((type) => {
    const held = badges.some((b) => b.badge_type === type && b.status === 'active');
    return {
      type,
      label: BADGE_TYPES[type].label,
      held,
      line: held ? 'issued' : (forecastLine(forecast, type) ?? 'not issued'),
    };
  });
}

/** The evidence line beside a badge nobody holds: what is still missing, or that it is ready. */
export function forecastLine(forecast: readonly Forecast[] | undefined, type: BadgeType): string | null {
  const row = forecast?.find((f) => f.badge_type === type);
  if (!row) return null;
  return row.issuable ? 'evidence on file — ready to issue' : refusal(forecast, type);
}

/**
 * Where one badge stands (doc 05-admin/06 §3.5): its active row when there is one, else its most
 * recent row whatever became of it — a dead badge is shown dead, never hidden as "not issued".
 */
export function badgeStanding<R extends BadgeRecord>(
  rows: readonly R[],
  type: BadgeType,
): { state: BadgeState; row: R | undefined } {
  const mine = rows.filter((b) => b.badge_type === type).sort((a, b) => b.id - a.id);
  const row = mine.find((b) => b.status === 'active') ?? mine[0];
  return { state: row ? (row.status as BadgeState) : 'not issued', row };
}

/** The state line beside each badge, in the doc's words. */
export function stateLine(state: BadgeState, row: BadgeRecord | undefined): string {
  if (!row) return 'never issued';
  const reason = row.revocation_reason ?? 'no reason recorded';
  if (state === 'active')
    return `active since ${row.issued_on}${row.issued_by ? ` by ${row.issued_by}` : ''}${row.expires_on ? ` · until ${row.expires_on}` : ''}`;
  if (state === 'expired') return `expired ${row.expires_on ?? ''}`.trim();
  return `${state}: ${reason}`;
}

/** The one aggregate the brand allows: a passive count, never a control. */
export function activeCount(rows: readonly BadgeRecord[]): number {
  return new Set(rows.filter((b) => b.status === 'active').map((b) => b.badge_type)).size;
}

export function evidenceOptions(
  c: { documents: readonly { id: number; doc_type: string }[]; references: readonly { id: number; referee_name: string }[]; background_checks: readonly { id: number; provider_name: string }[] },
  badgeType: BadgeType,
): string[] {
  const field = EVIDENCE_KIND[badgeType].field;
  if (field === 'evidence_document_id') return c.documents.map((d) => `${d.id} · ${d.doc_type.replace(/_/g, ' ')}`);
  if (field === 'evidence_reference_check_id') return c.references.map((r) => `${r.id} · ${r.referee_name}`);
  if (field === 'evidence_background_check_id') return c.background_checks.map((b) => `${b.id} · ${b.provider_name}`);
  /* `temple_authorized` points at a temple agreement, and doc 14 has not built that table. The
     server refuses the badge outright, so offering a picker would be offering a dead end. */
  return [];
}
